import type { HydratedEntity } from "@1js/pl-types";
import { type SendApiActionEventParameter } from "../utils/Telemetry";
import type { ApiName, HttpStatusCode } from "../utils/Telemetry/config";
import type { ApiError } from "./fetch";

/**
 * Retrieves the value of the "X-Supportservice-Datacenter" header from the response headers
 * @param headers The response headers from which to extract the datacenter value
 * @returns The value of the "X-Supportservice-Datacenter" header, or an empty string if not found
 */
export function getSupportServiceDatacenter(headers: Headers): string {
  return headers.get("X-Supportservice-Datacenter") || "";
}

/**
 * Logs an API error by converting it to boolean
 * @param apiName Api to log error for
 * @param error original error that gets parsed and converted
 * @param logicalId Logical Id of the request
 * @param onSendApiAction Callback method from telemetry hook to send telemetry
 */
export function logApiError(
  apiName: ApiName,
  error: Error,
  logicalId: string,
  supportServiceLatency: number,
  onSendApiAction: (params: SendApiActionEventParameter) => void
) {
  const response: Response | undefined = (error as ApiError)?.response;
  onSendApiAction({
    apiName,
    status: response?.status as HttpStatusCode,
    isInit: false,
    isFromCache: false,
    supportServiceLatency,
    traceId: response?.headers?.get("x-datasource-traceid") || logicalId,
    supportServiceDatacenter:
      response && response.headers
        ? getSupportServiceDatacenter(response.headers)
        : "",
  });
}

/**
 * Extracts HydratedEntities from the input string
 * @param input - The input string
 * @returns A list of HydratedEntities
 */
export const extractHydratedEntities = (
  input: string | null | undefined
): HydratedEntity[] | undefined => {
  if (!input) return undefined;

  const regex =
    /<entity type="(File|People|Event)" ReferenceId="([^"]+)">([^<]+)<\/entity>/g;
  const entities = [] as HydratedEntity[];
  let match;

  while ((match = regex.exec(input)) !== null) {
    const type = match[1];
    const referenceId = match[2];
    const displayName = match[3];

    if (type === "File") {
      entities.push({
        Type: type,
        Entity: {
          Id: referenceId,
          ReferenceId: referenceId,
          FileName: displayName,
          SpoId: referenceId,
        },
      });
    } else if (type === "People") {
      entities.push({
        Type: type,
        Entity: {
          Id: referenceId,
          ReferenceId: referenceId,
          EmailAddresses: [referenceId],
          DisplayName: displayName,
        },
      });
    } else if (type === "Event") {
      entities.push({
        Type: type,
        Entity: {
          Id: "",
          ReferenceId: referenceId,
          Subject: displayName,
        },
      });
    }
  }

  return entities.length > 0 ? entities : undefined;
};
